/**
 * 管理员 OKX 订单管理标签页
 */

import { useState, useEffect } from 'react';
import { apiClient } from '@/services/apiClient';
import './AdminOKXOrdersTab.css';

interface OKXOrder {
  id: string;
  user_id: string | null;
  operator_id: string | null;
  user_email: string;
  operator_name: string;
  okx_order_id: string;
  okx_deposit_address: string;
  amount: number;
  currency: string;
  chain: string;
  status: 'pending' | 'paid' | 'expired' | 'failed';
  created_at: string;
  paid_at: string | null;
  expired_at: string | null;
  deposit_order_id: string | null;
  deposit_status: number | null;
  deposit_bonus_amount: number | null;
  tx_id: string | null;
}

interface OrderStats {
  total_orders: number;
  pending_orders: number;
  paid_orders: number;
  expired_orders: number;
  failed_orders: number;
  total_amount: number;
  paid_amount: number;
  today_orders: number;
  today_amount: number;
}

const statusConfig = {
  pending: { label: '待支付', color: '#faad14', bgColor: '#fffbe6' },
  paid: { label: '已支付', color: '#52c41a', bgColor: '#f6ffed' },
  expired: { label: '已过期', color: '#8c8c8c', bgColor: '#f5f5f5' },
  failed: { label: '失败', color: '#ff4d4f', bgColor: '#fff1f0' },
};

export default function AdminOKXOrdersTab() {
  const [orders, setOrders] = useState<OKXOrder[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // OKX 订单类型标签页
  const [okxOrderType, setOkxOrderType] = useState<'user' | 'operator'>('user');
  
  // 筛选和搜索
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // 分页
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // 详情模态框
  const [selectedOrder, setSelectedOrder] = useState<OKXOrder | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // 加载订单列表
  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchText, currentPage, okxOrderType]);

  // 加载统计信息（只在初始加载和状态筛选变化时）
  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, okxOrderType]);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.getAdminOKXOrders({
        user_id: null,
        operator_id: null,
        order_type: okxOrderType === 'user' ? 'user' : 'operator',
        status: statusFilter || null,
        search: searchText || null,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      });

      if (response.success && response.data) {
        setOrders(response.data);
        setTotalCount(response.data.length);
      } else {
        setError(response.message || '加载订单失败');
      }
    } catch (err) {
      console.error('Failed to load OKX orders:', err);
      setError(err instanceof Error ? err.message : '加载订单失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiClient.getAdminOKXOrdersStats({
        order_type: okxOrderType === 'user' ? 'user' : 'operator'
      });

      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleSearch = () => {
    setSearchText(searchInput);
    setCurrentPage(1);
  };

  const handleViewDetail = (order: OKXOrder) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('已复制到剪贴板');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const formatAmount = (amount: number) => {
    return amount.toFixed(2);
  };

  return (
    <div className="admin-okx-orders-tab">
      {/* OKX 订单类型子标签页 */}
      <div className="okx-sub-tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #e8e8e8' }}>
        <button
          onClick={() => {
            setOkxOrderType('user');
            setCurrentPage(1);
          }}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: okxOrderType === 'user' ? '2px solid #1890ff' : '2px solid transparent',
            background: 'none',
            color: okxOrderType === 'user' ? '#1890ff' : '#666',
            fontWeight: okxOrderType === 'user' ? 600 : 400,
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          用户 OKX 订单
        </button>
        <button
          onClick={() => {
            setOkxOrderType('operator');
            setCurrentPage(1);
          }}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: okxOrderType === 'operator' ? '2px solid #1890ff' : '2px solid transparent',
            background: 'none',
            color: okxOrderType === 'operator' ? '#1890ff' : '#666',
            fontWeight: okxOrderType === 'operator' ? 600 : 400,
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          运营商 OKX 订单
        </button>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">总订单数</div>
            <div className="stat-value">{stats.total_orders}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">待支付</div>
            <div className="stat-value" style={{ color: statusConfig.pending.color }}>
              {stats.pending_orders}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">已支付</div>
            <div className="stat-value" style={{ color: statusConfig.paid.color }}>
              {stats.paid_orders}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">总金额</div>
            <div className="stat-value">${formatAmount(stats.total_amount)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">已支付金额</div>
            <div className="stat-value" style={{ color: statusConfig.paid.color }}>
              ${formatAmount(stats.paid_amount)}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">今日订单</div>
            <div className="stat-value">{stats.today_orders}</div>
          </div>
        </div>
      )}

      {/* 筛选和搜索 */}
      <div className="filter-bar">
        <div className="filter-group">
          <label>状态筛选：</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">全部</option>
            <option value="pending">待支付</option>
            <option value="paid">已支付</option>
            <option value="expired">已过期</option>
            <option value="failed">失败</option>
          </select>
        </div>

        <div className="search-group">
          <input
            type="text"
            placeholder={okxOrderType === 'user' ? '搜索订单ID、用户邮箱、充值地址...' : '搜索订单ID、运营商名称、充值地址...'}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>搜索</button>
          <button onClick={loadOrders}>刷新</button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="alert alert-error">
          ❌ {error}
        </div>
      )}

      {/* 订单列表 */}
      {loading ? (
        <div className="loading-state">加载中...</div>
      ) : orders.length === 0 ? (
        <div className="empty-state">暂无订单</div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>订单ID</th>
                <th>{okxOrderType === 'user' ? '用户' : '运营商'}</th>
                <th>金额</th>
                <th>币种/网络</th>
                <th>状态</th>
                <th>创建时间</th>
                <th>支付时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <code className="order-id">{order.okx_order_id.substring(0, 16)}...</code>
                  </td>
                  <td>{okxOrderType === 'user' ? order.user_email : order.operator_name}</td>
                  <td className="amount">${formatAmount(order.amount)}</td>
                  <td>
                    {order.currency}
                    <br />
                    <small>{order.chain}</small>
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{
                        color: statusConfig[order.status].color,
                        backgroundColor: statusConfig[order.status].bgColor
                      }}
                    >
                      {statusConfig[order.status].label}
                    </span>
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>{formatDate(order.paid_at)}</td>
                  <td>
                    <button
                      className="btn-link"
                      onClick={() => handleViewDetail(order)}
                    >
                      查看详情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 分页 */}
      {totalCount > pageSize && (
        <div className="pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            上一页
          </button>
          <span>第 {currentPage} 页</span>
          <button
            disabled={orders.length < pageSize}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            下一页
          </button>
        </div>
      )}

      {/* 订单详情模态框 */}
      {showDetailModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>订单详情</h4>
              <button className="btn-close" onClick={() => setShowDetailModal(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h5>基本信息</h5>
                <div className="detail-row">
                  <span className="label">订单状态：</span>
                  <span
                    className="status-badge"
                    style={{
                      color: statusConfig[selectedOrder.status].color,
                      backgroundColor: statusConfig[selectedOrder.status].bgColor
                    }}
                  >
                    {statusConfig[selectedOrder.status].label}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">{okxOrderType === 'user' ? '用户邮箱：' : '运营商名称：'}</span>
                  <span>{okxOrderType === 'user' ? selectedOrder.user_email : selectedOrder.operator_name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">充值金额：</span>
                  <span className="amount">${formatAmount(selectedOrder.amount)} {selectedOrder.currency}</span>
                </div>
                <div className="detail-row">
                  <span className="label">区块链网络：</span>
                  <span>{selectedOrder.chain}</span>
                </div>
                {selectedOrder.deposit_bonus_amount && selectedOrder.deposit_bonus_amount > 0 && (
                  <div className="detail-row">
                    <span className="label">赠金金额：</span>
                    <span style={{ color: '#52c41a' }}>
                      +${formatAmount(selectedOrder.deposit_bonus_amount)}
                    </span>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h5>充值地址</h5>
                <div className="copy-field">
                  <code>{selectedOrder.okx_deposit_address}</code>
                  <button onClick={() => copyToClipboard(selectedOrder.okx_deposit_address)}>
                    复制
                  </button>
                </div>
              </div>

              {selectedOrder.tx_id && (
                <div className="detail-section">
                  <h5>交易哈希</h5>
                  <div className="copy-field">
                    <code>{selectedOrder.tx_id}</code>
                    <button onClick={() => copyToClipboard(selectedOrder.tx_id!)}>
                      复制
                    </button>
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h5>时间信息</h5>
                <div className="detail-row">
                  <span className="label">创建时间：</span>
                  <span>{formatDate(selectedOrder.created_at)}</span>
                </div>
                {selectedOrder.paid_at && (
                  <div className="detail-row">
                    <span className="label">支付时间：</span>
                    <span>{formatDate(selectedOrder.paid_at)}</span>
                  </div>
                )}
                {selectedOrder.expired_at && (
                  <div className="detail-row">
                    <span className="label">过期时间：</span>
                    <span>{formatDate(selectedOrder.expired_at)}</span>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h5>订单ID</h5>
                <code className="order-id-full">{selectedOrder.okx_order_id}</code>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailModal(false)}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
